import { Track } from "@/src/lib/tracking";
/**
 * Fetch User Folders Handler - 获取用户收藏夹列表处理器
 *
 * 负责处理 fetchUserFolders API 的完整逻辑
 */

import type { FolderInfo, OperationResult } from "../types";

import { CacheManager } from "../managers/CacheManager";
import { PositionAccountStatusEnum } from "@features/api/memberApi";
import type { PositionsAccountService } from "../PositionsAccountService";
import { UserStateManager } from "../managers/UserStateManager";

export interface FetchUserFoldersOptions {
  limit?: number; // Limit return count, default 20, max 20
  forceRefresh?: boolean; // Whether to force refresh cache
  cacheTimeout?: number; // Cache timeout in milliseconds, default 5 minutes
}

export class FetchUserFoldersHandler {
  constructor(
    private userStateManager: UserStateManager,
    private cacheManager: CacheManager,
    private positionsAccountService: PositionsAccountService | null,
    private sortUserFoldersByCreatedAt: (folders: FolderInfo[]) => FolderInfo[],
    private handleUserSwitch: () => Promise<void>,
    private getCurrentUserId: () => string | null,
    private setStateLoading: (loading: boolean) => void,
    private setStateError: (error: string | null) => void,
    private handleError: (error: Error) => void,
  ) {}

  /**
   * Execute fetch user folders
   */
  async execute(
    options?: FetchUserFoldersOptions,
  ): Promise<OperationResult<FolderInfo[]>> {
    try {
      // Clear previous error state
      this.setStateError(null);
      this.setStateLoading(true);

      // 1. Check user switch
      await this.handleUserSwitch();

      const currentUserId = this.getCurrentUserId();
      Track.log("info", "FetchUserFoldersHandler", ":", {
        cause: String(currentUserId),
      });

      if (!currentUserId) {
        this.setStateLoading(false);
        return {
          success: false,
        };
      }

      // 2. Check cache logic
      const cacheTimeout = options?.cacheTimeout || 5 * 60 * 1000; // Default 5 minutes cache
      const now = Date.now();

      // 3. Cache check: if valid cache exists and not forcing refresh, return cached data directly
      if (
        !options?.forceRefresh &&
        this.cacheManager.isUserFoldersCacheValid(cacheTimeout)
      ) {
        const cachedFolders = this.cacheManager.getCachedUserFolders();
        if (cachedFolders) {
          // Restore data from cache to active state (deep copy to ensure reactive update)
          this.userStateManager.updateUserFolders(
            cachedFolders.map((folder) => ({ ...folder })),
          );
          this.setStateLoading(false);
          return {
            success: true,
            data: cachedFolders,
          };
        }
      }

      // 并行请求用户账户和经纪商列表
      const [result, brokersResult] = await Promise.all([
        this.positionsAccountService?.getUserAccounts({}) ??
          Promise.resolve(null),
        this.positionsAccountService?.getPositionBrokers() ??
          Promise.resolve(null),
      ]);

      if (!result) {
        return { success: false, error: "Failed to get user accounts" };
      }

      // brokers 即使失败也不影响主流程
      const brokers = brokersResult?.data || [];

      if (result.success && result.data && result.data.rows) {
        // 5. Transform data format
        const folders: FolderInfo[] = result?.data?.rows?.map((item) => {
          let slug, name, broker_id, icon_url;

          // Parse broker info for non-EDGEN platforms
          if (item.platform !== "EDGEN" && item.broker) {
            try {
              const brokerData = JSON.parse(item?.broker);
              slug = brokerData.slug;
              name = brokerData.name || brokerData.displayName;

              // Match broker in brokers list (prioritize slug, fallback to name)
              const matched = brokers.find(
                (b: any) =>
                  b.slug === brokerData.slug || b.name === brokerData.name,
              );

              if (matched) {
                broker_id = matched.broker_id;
                icon_url = matched.broker_icon;
              }

              // Fallback: use logo from broker JSON if not matched
              if (!icon_url) {
                icon_url =
                  brokerData.awsS3LogoUrl || brokerData.awsS3SquareLogoUrl;
              }
            } catch (error) {
              Track.error(error, {
                tag: "FetchUserFoldersHandler",
                msg: "Failed to parse broker JSON:",
              });
            }
          }

          return {
            id: item.id?.toString() || "",
            name: name || "",
            slug,
            broker_id,
            icon_url,
            created_at: item.created
              ? new Date(item.created).toISOString()
              : new Date().toISOString(),
            updated_at: item.modified
              ? new Date(item.modified).toISOString()
              : new Date().toISOString(),
            asset_count: item.asset_count || 0,
            platform: item.platform,
            platform_user_id: item.platform_user_id,
            status: item.status || PositionAccountStatusEnum.ACTIVE,
            last_synced_time: item.last_synced_time,
            account_type: item.account_type,
            // account_mask: item.account_mask,
          };
        });

        // 6. Update cache data
        const sortedFolders = this.sortUserFoldersByCreatedAt(folders);
        const userData =
          this.userStateManager.getOrCreateUserData(currentUserId);
        userData.userFolders = [...sortedFolders]; // Source data uses copy
        userData.lastAccess = now;
        this.userStateManager.updateUserFolders(sortedFolders); // Active data uses sorted array

        // Clear loading state
        this.setStateLoading(false);
        return {
          success: true,
          data: sortedFolders,
        };
      } else {
        const error = result.message;
        this.setStateError(error || null);
        this.setStateLoading(false);
        return {
          success: false,
          error,
        };
      }
    } catch (error) {
      const errorMessage = "refetch again";
      this.setStateError(errorMessage);
      this.setStateLoading(false);
      this.handleError(error as Error);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}
